import { useState, useEffect } from 'react';
import { Building2, Save, Sliders, Monitor, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';

interface TenantSettings {
  nome_fantasia: string;
  razao_social: string;
  cnpj: string;
  inscricao_estadual: string;
  endereco: string;
  telefone: string;
  dias_vencimento_fiado: number;
  percentual_max_desconto: number;
  nivel_minimo_estoque_padrao: number;
  tempo_limite_qrcode_pix: number;
}

interface CashRegister {
  id: string;
  name: string;
}

// ─── Masks ───────────────────────────────────────────────────────────────────

function maskCNPJ(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
}

export function SettingsPage() {
  const [settings, setSettings] = useState<TenantSettings>({
    nome_fantasia: '',
    razao_social: '',
    cnpj: '',
    inscricao_estadual: '',
    endereco: '',
    telefone: '',
    dias_vencimento_fiado: 30,
    percentual_max_desconto: 10,
    nivel_minimo_estoque_padrao: 5,
    tempo_limite_qrcode_pix: 300,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/api/settings').then(res => {
      if (res.ok) return res.json();
      return null;
    }).then(data => {
      if (data) setSettings(data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Caixas (terminais)
  const [registers, setRegisters] = useState<CashRegister[]>([]);
  const [newRegisterName, setNewRegisterName] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('fluxopdv_registers');
    if (saved) {
      try {
        setRegisters(JSON.parse(saved));
      } catch {
        setRegisters([{ id: '1', name: 'Caixa 1' }]);
      }
    } else {
      setRegisters([{ id: '1', name: 'Caixa 1' }]);
    }
  }, []);

  const addRegister = () => {
    if (!newRegisterName.trim()) {
      toast.error('Informe o nome do caixa');
      return;
    }
    const updated = [...registers, { id: Date.now().toString(), name: newRegisterName.trim() }];
    setRegisters(updated);
    localStorage.setItem('fluxopdv_registers', JSON.stringify(updated));
    setNewRegisterName('');
    toast.success('Caixa cadastrado');
  };

  const removeRegister = (id: string) => {
    if (registers.length <= 1) {
      toast.error('É necessário ter pelo menos 1 caixa');
      return;
    }
    const updated = registers.filter(r => r.id !== id);
    setRegisters(updated);
    localStorage.setItem('fluxopdv_registers', JSON.stringify(updated));
    toast.success('Caixa removido');
  };

  const handleSave = async () => {
    if (!settings.nome_fantasia.trim()) {
      toast.error('Nome Fantasia é obrigatório');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify(settings),
      });
      if (res.ok) {
        localStorage.setItem('fluxopdv_registers', JSON.stringify(registers));
        toast.success('Configurações salvas com sucesso');
      } else {
        toast.error('Erro ao salvar configurações');
      }
    } catch {
      toast.error('Erro de conexão');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof TenantSettings, value: string | number) => {
    setSettings({ ...settings, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Configurações</h1>
        <p className="text-base text-muted-foreground">
          Dados do estabelecimento e parâmetros operacionais
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Dados do Estabelecimento */}
        <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="rounded-lg bg-primary/10 p-2">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Dados do Estabelecimento</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Nome Fantasia *</label>
              <input
                type="text"
                value={settings.nome_fantasia}
                onChange={(e) => updateField('nome_fantasia', e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                placeholder="Ex: Mercadinho do Zé"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Razão Social</label>
              <input
                type="text"
                value={settings.razao_social}
                onChange={(e) => updateField('razao_social', e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                placeholder="Ex: José da Silva ME"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground">CNPJ</label>
                <input
                  type="text"
                  value={settings.cnpj}
                  onChange={(e) => updateField('cnpj', maskCNPJ(e.target.value))}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Inscrição Estadual</label>
                <input
                  type="text"
                  value={settings.inscricao_estadual}
                  onChange={(e) => updateField('inscricao_estadual', e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                  placeholder="Isento ou número"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Endereço</label>
              <input
                type="text"
                value={settings.endereco}
                onChange={(e) => updateField('endereco', e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                placeholder="Rua, número, bairro"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Telefone</label>
              <input
                type="text"
                value={settings.telefone}
                onChange={(e) => updateField('telefone', maskPhone(e.target.value))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                placeholder="(00) 00000-0000"
                maxLength={15}
              />
            </div>
          </div>
        </div>

        {/* Parâmetros Operacionais */}
        <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="rounded-lg bg-cyan-500/10 p-2">
              <Sliders className="h-5 w-5 text-cyan-500" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Parâmetros Operacionais</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Dias para vencimento do crédito</label>
              <p className="text-xs text-muted-foreground mb-1">
                Prazo padrão para pagamento de compras no crédito (fiado)
              </p>
              <input
                type="number"
                value={settings.dias_vencimento_fiado}
                onChange={(e) => updateField('dias_vencimento_fiado', Number(e.target.value))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                min={1}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Desconto máximo sem autorização (%)</label>
              <p className="text-xs text-muted-foreground mb-1">
                Acima deste valor, requer senha do gerente
              </p>
              <input
                type="number"
                value={settings.percentual_max_desconto}
                onChange={(e) => updateField('percentual_max_desconto', Number(e.target.value))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                min={0}
                max={100}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Nível mínimo de estoque padrão</label>
              <p className="text-xs text-muted-foreground mb-1">
                Alerta quando estoque atingir este nível
              </p>
              <input
                type="number"
                value={settings.nivel_minimo_estoque_padrao}
                onChange={(e) => updateField('nivel_minimo_estoque_padrao', Number(e.target.value))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                min={1}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Tempo limite QR Code PIX (segundos)</label>
              <p className="text-xs text-muted-foreground mb-1">
                Tempo de expiração do QR Code de pagamento
              </p>
              <input
                type="number"
                value={settings.tempo_limite_qrcode_pix}
                onChange={(e) => updateField('tempo_limite_qrcode_pix', Number(e.target.value))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                min={60}
                step={30}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Botão Salvar */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-medium text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {saving ? 'Salvando...' : 'Salvar Configurações'}
      </button>

      {/* Caixas (Terminais) */}
      <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="rounded-lg bg-amber-500/10 p-2">
            <Monitor className="h-5 w-5 text-amber-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Caixas (Terminais)</h2>
            <p className="text-sm text-muted-foreground">Cadastre os caixas do seu estabelecimento</p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          {registers.map((reg) => (
            <div key={reg.id} className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="flex items-center gap-3">
                <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
                <span className="text-base font-medium text-foreground">{reg.name}</span>
              </div>
              <button
                onClick={() => removeRegister(reg.id)}
                className="rounded p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                aria-label={`Remover ${reg.name}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={newRegisterName}
            onChange={(e) => setNewRegisterName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addRegister()}
            placeholder="Nome do novo caixa (ex: Caixa 2)"
            className="flex-1 rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          />
          <button
            onClick={addRegister}
            className="flex items-center gap-1 rounded-lg bg-primary px-4 py-2.5 text-base font-medium text-white hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Adicionar
          </button>
        </div>
      </div>
    </div>
  );
}
